import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Divider,
  Button,
  Avatar,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { NAVBAR_HEIGHT } from "../../../../components/Navbar/NavDesktop";
import { usePageMetadata } from "../../../../context/PageMetadataContext";
import {
  getUserAttributes,
  addEditUserAttribute,
} from "../../../../services/api/apiClient";
import {
  GetUserAttributesParams,
  UserAttributeResult,
  EditUserAttributes,
} from "../../../../services/api/types/portalConfiguration/userAttribute";

import { usePortal } from "../../../../context/PortalsContext";
import { useLanguages } from "../../../../context/LanguagesContext";
import { useToast } from "../../../../context/ToastContext";

import { AttributeRow } from "./components/AttributeRow";
import { brand } from "../../../../theme/themePrimitives";
import userOutlined from "../../../../assets/user-oulined.svg";
import { ViewDetailsModal } from "./components/ViewDetailsModal";
import { GetUserResult } from "../../../../services/api/types/users/users";
import { ViewDetailsItem } from "./components/ViewDetailsModal";

const MANUAL_SELECTED_USERS_KEY = "manualSelectedUsers";
const FILTERED_SELECTED_USERS_KEY = "filteredSelectedUsers";
const SELECTED_USERS_SOURCE_KEY = "selectedUsersSource";
const LEGACY_SELECTED_USERS_KEY = "selectedUsers";

type AttributeErrors = {
  attributeKey: Record<number, string>;
  attributeValue: Record<number, string>;
};

export const EditAttributes = () => {
  const { portals } = usePortal();
  const { portalUid } = useParams();
  const { languages } = useLanguages();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [attributes, setAttributes] = useState<EditUserAttributes[]>([
    { attributeKey: "", attributeValue: "" },
  ]);
  const [attributesResult, setAttributesResult] = useState<
    UserAttributeResult[]
  >([]);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [openViewDetailsModal, setOpenViewDetailsModal] = useState(false);
  const navigate = useNavigate();

  const [selectedUsers, setselectedUsers] = useState<GetUserResult[]>([]);

  const [errors, setErrors] = useState<AttributeErrors>({
    attributeKey: {},
    attributeValue: {},
  });

  const userText = selectedUsers.length > 1 ? "users" : "user";

  const selectedIds = selectedUsers.map(
    (selectedUser) => selectedUser.UserPortalUid,
  );

  const addRow = () => {
    setAttributes([...attributes, { attributeKey: "", attributeValue: "" }]);
  };
  const removeRow = (index: number) => {
    if (index === 0) return;
    setAttributes(attributes.filter((_, i) => i !== index));
  };

  const handleChange = <K extends keyof EditUserAttributes>(
    index: number,
    field: K,
    value: EditUserAttributes[K],
    selectedAttr?: UserAttributeResult,
  ) => {
    const newAttributes = [...attributes];
    if (field === "attributeKey") {
      newAttributes[index] = {
        ...newAttributes[index],
        attributeKey: selectedAttr?.attributeKey || (value as string),
        attributeValue: "",
      };
      setErrors((prev) => ({
        ...prev,
        attributeKey: { ...prev.attributeKey, [index]: "" },
        attributeValue: { ...prev.attributeValue, [index]: "" },
      }));
    } else {
      newAttributes[index] = {
        ...newAttributes[index],
        [field]: value,
      };

      setErrors((prev) => ({
        ...prev,
        [field]: { ...prev[field], [index]: "" },
      }));
    }
    setAttributes(newAttributes);
  };

  const validateAttributes = () => {
    const newErrors: AttributeErrors = {
      attributeKey: {},
      attributeValue: {},
    };
    let isValid = true;

    attributes.forEach((attr, i) => {
      if (!attr.attributeKey) {
        newErrors.attributeKey[i] = "Please select an attribute";
        isValid = false;
      }
      const isEmptyValue = Array.isArray(attr.attributeValue)
        ? attr.attributeValue.length === 0
        : !attr.attributeValue;
      if (isEmptyValue) {
        newErrors.attributeValue[i] = "Please enter a value";
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const fetchAttributes = async (
    params: GetUserAttributesParams = {
      languageCode: languages.selectedLanguage,
      short: true,
      isBulkEdit: true,
    },
  ) => {
    try {
      setLoading(true);
      if (!portals.selectedPortal) {
        return;
      }
      const response = await getUserAttributes(
        portals.selectedPortal.portalUid,
        params,
      );

      setAttributesResult(response.rows ?? []);
    } catch (error: any) {
      console.log("Error:", error);
      const data = error?.response?.data;

      const msg =
        data?.message ??
        (typeof data === "string" ? data : null) ??
        error?.message ??
        "Failed to fetch attributes";

      setAttributesResult([]);

      if (error.response.data === "No user attributes found!") return;
      showToast(msg, "error");
    } finally {
      setLoading(false);
    }
  };
  const handleAddEditUserAttribute = async () => {
    setButtonLoading(true);
    try {
      const isValid = validateAttributes();

      if (!isValid) {
        console.warn("Validation failed — fix errors before API call");
        return;
      }

      if (portalUid) {
        const formattedAttributes = attributes.map((attr) => {
          const selectedAttr = attributesResult.find(
            (item) => item.attributeKey === attr.attributeKey,
          );

          // Handle role attribute (pipe separated)
          if (
            attr.attributeKey === "role" &&
            Array.isArray(attr.attributeValue)
          ) {
            return {
              ...attr,
              attributeValue: attr.attributeValue.join("|"),
            };
          }

          // Handle isLineManager string attributes (pipe separated)
          if (
            selectedAttr?.isLineManager &&
            selectedAttr.dataType === "string" &&
            Array.isArray(attr.attributeValue)
          ) {
            return {
              ...attr,
              attributeValue: attr.attributeValue.join("|"),
            };
          }

          // everything else stays the same
          return {
            ...attr,
            attributeValue: Array.isArray(attr.attributeValue)
              ? attr.attributeValue.join(", ")
              : attr.attributeValue,
          };
        });
        const response = await addEditUserAttribute(portalUid, {
          users: selectedIds,
          attributes: formattedAttributes,
        });
        if (response === "OK") {
          showToast("Attributes updated successfully", "success");
          navigate(-1);
        }
      }
    } catch (error: any) {
      console.log("Error:", error);
      showToast(error.response?.data || "Something went wrong.", "error");
    } finally {
      setButtonLoading(false);
    }
  };

  useEffect(() => {
    if (portals.selectedPortal && languages.selectedLanguage) {
      fetchAttributes({
        languageCode: languages.selectedLanguage,
        short: true,
        isBulkEdit: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portals.selectedPortal, languages.selectedLanguage]);

  useEffect(() => {
    let parseErrorShown = false;

    const parseUsers = (key: string): GetUserResult[] => {
      try {
        const stored = localStorage.getItem(key);
        if (!stored) return [];
        const parsed = JSON.parse(stored);
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        console.log(`Error parsing ${key} from localStorage`, error);
        if (!parseErrorShown) {
          parseErrorShown = true;
          showToast("Error reading saved user selection.", "error");
        }
        return [];
      }
    };

    const source = localStorage.getItem(SELECTED_USERS_SOURCE_KEY) as
      | "manual"
      | "filtered"
      | null;

    const manualUsers = parseUsers(MANUAL_SELECTED_USERS_KEY);
    const filteredUsers = parseUsers(FILTERED_SELECTED_USERS_KEY);
    const legacyUsers = parseUsers(LEGACY_SELECTED_USERS_KEY);

    let usersToApply: GetUserResult[] = [];

    if (source === "filtered") {
      usersToApply = filteredUsers.length ? filteredUsers : legacyUsers;
    } else if (source === "manual") {
      usersToApply = manualUsers;
    } else if (filteredUsers.length) {
      usersToApply = filteredUsers;
    } else if (manualUsers.length) {
      usersToApply = manualUsers;
    } else {
      usersToApply = legacyUsers;
    }

    setselectedUsers(usersToApply);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  usePageMetadata(
    {
      title: "Edit attributes",
      breadcrumbs: [
        {
          label: "User management",
          path: `/portal-configuration`,
        },
        {
          label: "User search",
          path: `/portal-configuration/${portalUid}/users`,
        },
        {
          label: "Edit attributes",
        },
      ],
    },
    [portalUid],
  );

  return (
    <>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",

            height: `calc(100vh - ${NAVBAR_HEIGHT})`,
          }}
        >
          <CircularProgress size={100} />
        </Box>
      ) : (
        <Box>
          <Paper
            elevation={1}
            sx={{
              px: "24px",
              py: "20px",
              boxShadow:
                "0 1px 3px 0 rgba(16, 24, 40, 0.10), 0 1px 2px 0 rgba(16, 24, 40, 0.06)",
              mb: "10px",
            }}
          >
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto" }}>
              <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr" }}>
                <Avatar
                  sx={{
                    backgroundColor: "#F2F4F7",
                    padding: "10px",
                    borderRadius: "40px",
                    width: "48px",
                    height: "48px",
                  }}
                >
                  <img src={userOutlined} alt="selected user icon" />
                </Avatar>
                <Box sx={{ pl: "12px", display: "grid", alignItems: "center" }}>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    sx={{ fontSize: "16px" }}
                  >
                    {selectedUsers.length} {userText} selected
                  </Typography>
                  <Typography variant="body2">
                    Changes will apply to all selected {userText}
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Button
                  sx={{
                    border: `1px solid ${brand[300]}`,
                    boxShadow: "0 1px 2px 0 rgba(16, 24, 40, 0.05)",
                    color: `${brand[600]}`,
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                  onClick={() => setOpenViewDetailsModal(true)}
                >
                  <VisibilityOutlinedIcon fontSize="small" />
                  <Typography sx={{ pl: "5px" }}>View details</Typography>
                </Button>
              </Box>
            </Box>
          </Paper>
          <Paper
            elevation={1}
            sx={{
              px: "24px",
              py: "20px",
              boxShadow:
                "0 1px 3px 0 rgba(16, 24, 40, 0.10), 0 1px 2px 0 rgba(16, 24, 40, 0.06)",
            }}
          >
            <Typography
              variant="body2"
              fontWeight="bold"
              sx={{ fontSize: "16px" }}
            >
              Edit user attributes
            </Typography>
            <Divider sx={{ pb: "24px", mb: "24px" }} />

            {attributesResult && (
              <AttributeRow
                attributesResult={attributesResult}
                attributes={attributes}
                primaryButtonColor={brand[600]}
                addRow={addRow}
                removeRow={removeRow}
                handleChange={handleChange}
                errors={errors}
              />
            )}
            <Box textAlign="center">
              {buttonLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Button
                  sx={{
                    px: 2,
                    borderRadius: "12px",
                    backgroundColor: `${brand[600]} !important`,
                    color: "white",
                    "&:hover": {
                      bgcolor: `${brand[700]} !important`,
                    },
                  }}
                  onClick={handleAddEditUserAttribute}
                >
                  Update user attributes
                </Button>
              )}
            </Box>
          </Paper>
        </Box>
      )}
      {openViewDetailsModal && (
        <ViewDetailsModal
          setOpenViewDetailsModal={setOpenViewDetailsModal}
          openViewDetailsModal={openViewDetailsModal}
          title="Selected users"
          items={
            selectedUsers.map((user) => ({
              primary: user.firstName || "",
              secondary: user.email,
            })) as ViewDetailsItem[]
          }
        />
      )}
    </>
  );
};
